/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useMemo, useState } from "react";

import cn from "classnames";

import CircleCheck from "assets/Icons/CircleCheck";
import CircleExclamation from "assets/Icons/CircleExclamation";
import { UnGuard } from "constants/interfaces";
import { GetTripsResponse } from "lib/tripsApiTypes";
import ROUTES from "routes";
import { useUser } from "state/UserContext";
import getFormattedDate from "utils/getFormattedDate";

import { Button, ChevronRight, HR, Stack, Text } from "components";
import { useTranslation } from "react-i18next";
import { pushToDataLayer } from "utils/gtm";

import {
  CompanionSection,
  FlightSection,
  HeaderSection,
  MatchSection,
  StaySection,
} from "./Components";

import { ChevronDown, PlusIcon } from "components/Icons";

const formatDate = (date: string | null | undefined) => {
  if (!date) return null;
  return date.split("-").join("/");
};

const TripDetails = ({
  trip,
  type = "approved",
  number,
}: {
  trip: UnGuard<GetTripsResponse["approved_trips"]>[number];
  type?: "approved" | "pending";
  number: number;
}) => {
  const [isTripExpanded, setIsTripExpanded] = useState(true);
  const [isFlightExpanded, setIsFlightExpanded] = useState(false);
  const [isSessionExpanded, setIsSessionExpanded] = useState(false);
  const [isAccommodationExpanded, setIsAccommodationExpanded] = useState(false);

  const { t } = useTranslation();

  const TripState: Record<
    "approved" | "pending",
    Record<"bg-color" | "fg-color" | "text", string> & { icon: React.FC }
  > = {
    approved: {
      text: t("tripDetails.approved"),
      icon: CircleCheck,
      "bg-color": "-bg--theme-colors-success-card-bg",
      "fg-color": "-text--theme-colors-success-card-fg",
    },
    pending: {
      text: t("tripDetails.pending"),
      icon: CircleExclamation,
      "bg-color": "-bg--theme-colors-warning-card-bg",
      "fg-color": "-text--theme-colors-warning-card-fg",
    },
  };

  const { userData } = useUser();
  const rooms = useMemo(() => trip.reservations?.map((r) => r.rooms), [trip]);

  const formattedStartDate = formatDate(trip?.trip_dates?.start_date);
  const formattedLastDate = formatDate(trip?.trip_dates?.last_date);

  const tripState = TripState[type];

  return (
    <Stack className="relative w-full -bg--theme-colors-surface-neutral-level02 border -border--theme-colors-border-neutral-11 -rounded-outer-container px-6 md:px-14 py-10">
      <div
        className={cn(
          "flex  gap-x-2 absolute top-5 end-5 px-2 py-1 rounded-full",
          tripState["bg-color"],
        )}
      >
        <Text className={cn("text-xs", tripState["fg-color"])}>
          {tripState["text"]}
        </Text>

        <tripState.icon />
      </div>

      <HeaderSection
        number={number}
        date={
          formattedStartDate && formattedLastDate
            ? `${getFormattedDate(formattedStartDate)} - ${getFormattedDate(
                formattedLastDate,
              )}`
            : null
        }
      />

      <HR
        className="-border--theme-colors-border-neutral-09"
        isDashed={!isTripExpanded}
      />

      {!isTripExpanded && (
        <Stack className="sm:flex-row justify-between gap-y-10 sm:gap-y-0">
          <Stack className="min-w-0">
            <Text className="text-sm -text--theme-colors-text-subtitle-brand line-clamp-4 break-all pe-1">
              {t("tripDetails.tripCompanionsLabel")}
            </Text>
            <Text className="-text--theme-colors-text-body line-clamp-4 break-all pe-1">
              {trip?.companions?.length} {t("tripDetails.guestsLabel")}
            </Text>
          </Stack>

          <Stack className="min-w-0">
            <Text className="text-sm -text--theme-colors-text-subtitle-brand line-clamp-4 break-all pe-1">
              {t("tripDetails.flightDetailsLabel")}
            </Text>
            <Text className="-text--theme-colors-text-body line-clamp-4 break-all pe-1">
              {trip?.flights?.length} {t("tripDetails.ticketsLabel")}
            </Text>
          </Stack>

          <Stack className="min-w-0">
            <Text className="text-sm -text--theme-colors-text-subtitle-brand line-clamp-4 break-all pe-1">
              {t("tripDetails.accommodationDetailsLabel")}
            </Text>
            <Text className="-text--theme-colors-text-body line-clamp-4 break-all pe-1">
              {trip?.reservations?.length} {t("tripDetails.reservationsLabel")},{" "}
              {rooms?.length} {t("tripDetails.roomsLabel")}
            </Text>
          </Stack>
        </Stack>
      )}

      {isTripExpanded && (
        <Stack className="gap-y-2.5">
          {!!trip.companions?.length && (
            <CompanionSection title={undefined} companions={trip.companions} />
          )}

          {Object.keys(trip.sessions ?? {}).length > 0 && (
            <DetailCard
              isExpanded={isSessionExpanded}
              handleExpand={() => {
                pushToDataLayer({
                  event: isSessionExpanded
                    ? "trip_sessions_collapse"
                    : "trip_sessions_expand",
                  screen_name: "dashboard",
                  action: isSessionExpanded ? "collapse" : "expand",
                });
                setIsSessionExpanded(!isSessionExpanded);
              }}
              title={t("matchSection.sessionsLabel")}
              subTitle={t("matchSection.sessionsSubtitle")}
              handleAdd={undefined}
            >
              <MatchSection
                sessions={trip.sessions}
                blinkId={userData?.application?.blink_id!}
              />
            </DetailCard>
          )}

          {(!!trip.flights?.length || trip.will_add_flight) && (
            <DetailCard
              isExpanded={isFlightExpanded}
              hasData={!!trip.flights?.length}
              handleExpand={() => {
                pushToDataLayer({
                  event: isFlightExpanded
                    ? "trip_flights_collapse"
                    : "trip_flights_expand",
                  screen_name: "dashboard",
                  action: isFlightExpanded ? "collapse" : "expand",
                });
                setIsFlightExpanded(!isFlightExpanded);
              }}
              title={t("tripDetails.flightDetailsLabel")}
              subTitle={t("tripDetails.flightDetailsSubtitle")}
              handleAdd={
                trip.will_add_flight &&
                ROUTES.dashboard.flight(userData?.hash_id!, "new")
              }
            >
              {trip.flights?.map((flight, i) => (
                <FlightSection
                  key={i}
                  flight={flight}
                  trip={trip}
                  number={i + 1}
                />
              ))}
            </DetailCard>
          )}

          {(!!trip.reservations?.length || trip.will_add_reservation) && (
            <DetailCard
              isExpanded={isAccommodationExpanded}
              hasData={!!trip.reservations?.length}
              handleExpand={() => {
                pushToDataLayer({
                  event: isAccommodationExpanded
                    ? "trip_accommodations_collapse"
                    : "trip_accommodations_expand",
                  screen_name: "dashboard",
                  action: isAccommodationExpanded ? "collapse" : "expand",
                });
                setIsAccommodationExpanded(!isAccommodationExpanded);
              }}
              title={t("tripDetails.accommodationDetailsLabel")}
              subTitle={t("tripDetails.accommodationDetailsSubtitle")}
              handleAdd={
                trip.will_add_reservation &&
                ROUTES.dashboard.reservation(userData?.hash_id!, "new")
              }
            >
              <StaySection
                reservations={trip.reservations}
                userId={userData?.hash_id!}
                tripId={trip.id}
              />
            </DetailCard>
          )}
        </Stack>
      )}

      <div className="flex w-full items-center gap-x-12 mt-7 md:mt-5">
        <HR className="my-3 -border--theme-colors-action-primary-default-bg h-0" />

        <Button
          variant={"ghost-unstyled"}
          onClick={() => {
            pushToDataLayer({
              event: isTripExpanded
                ? "trip_details_collapse"
                : "trip_details_expand",
              screen_name: "dashboard",
              action: isTripExpanded ? "collapse" : "expand",
            });
            setIsTripExpanded(!isTripExpanded);
          }}
          className="text-sm"
        >
          <div className="flex gap-x-2">
            <Text>
              {isTripExpanded
                ? t("common.buttons.collapse")
                : t("common.buttons.expand")}
            </Text>

            <div>
              <ChevronRight
                className={cn(!isTripExpanded ? "rotate-90" : "-rotate-90")}
              />
            </div>
          </div>
        </Button>

        <HR className="my-3 -border--theme-colors-action-primary-default-bg h-0" />
      </div>
    </Stack>
  );
};

const DetailCard = ({
  isExpanded,
  hasData = true,
  handleExpand,
  title,
  subTitle,
  handleAdd,
  children,
}: {
  isExpanded: boolean;
  hasData?: boolean;
  handleExpand: () => void;
  title: string;
  subTitle: string;
  handleAdd?: string | false | undefined;
  children: React.ReactNode;
}) => {
  const { t } = useTranslation();
  return (
    <Stack className="border -border--theme-colors-border-neutral-09 -bg--theme-colors-surface-neutral-level04 -rounded-inner-container px-4 md:px-9 py-3 md:py-6 gap-y-5 md:gap-y-10 overflow-hidden">
      <Stack className="items-start">
        <div className="flex items-center justify-between w-full">
          <Button size="xs" variant="ghost-unstyled" onClick={handleExpand}>
            <div className="flex items-center gap-x-[5px]">
              {hasData ? (
                <ChevronDown
                  className={cn(
                    isExpanded ? "" : "rotate-180",
                    "-text--theme-colors-action-ghost-default-fg grow",
                  )}
                  height={30}
                  width={30}
                />
              ) : (
                <div className="h-[30px] w-[30px]" />
              )}

              <Text className="font-bold text-xl leading-6 -text--theme-colors-text-subtitle-brand text-start">
                {title}
              </Text>
            </div>
          </Button>

          {handleAdd && (
            <Button
              as="link"
              to={handleAdd}
              size="xs"
              variant="primary"
              className="!p-0"
            >
              <div className="flex justify-center items-center px-2 gap-x-2 md:w-36 md:h-10">
                <PlusIcon />
                <Text className="hidden sm:block">
                  {t("tripDetails.addNewLabel")}
                </Text>
              </div>
            </Button>
          )}
        </div>

        <Text className="text-xs -text---theme-colors-text-body ms-[35px]">
          {subTitle}
        </Text>
      </Stack>

      {isExpanded && children}
    </Stack>
  );
};

export default TripDetails;
